import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfig } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSaasWorkspacePolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyContinuousSchedulesTable, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfig) => void;
  onDelete?: (policy: BackupPolicyConfig) => void;
  onDuplicate?: (policy: BackupPolicyConfig) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfig) => Promise<boolean>;
}
export function BackupPolicyCard(props) {
  const $ = _c(173);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== allBackupPolicyProperties || $[1] !== enabled || $[2] !== isAuthorized || $[3] !== props || $[4] !== snackbar || $[5] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t15;
    if ($[28] !== props.policy.condition) {
      t15 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[28] = props.policy.condition;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    const resourcesScopeType = t15;
    let t16;
    if ($[30] !== props.policy.policyType) {
      t16 = isSaasWorkspacePolicyType(props.policy.policyType);
      $[30] = props.policy.policyType;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    const isSaasWorkspace = t16;
    let t17;
    if ($[32] !== props.policy.policyType) {
      t17 = isGoogleWorkspacePolicyType(props.policy.policyType);
      $[32] = props.policy.policyType;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const isGoogleWorkspace = t17;
    let t18;
    if ($[34] !== props || $[35] !== snackbar) {
      t18 = async () => {
        const enabled_0 = props.policy.enabled;
        const updated = await props.onEnabledPolicyUpdate?.(props.policy);
        if (updated === false) {
          return;
        }
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[34] = props;
      $[35] = snackbar;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    togglePolicyEnabledState = t18;
    let t19;
    if ($[37] !== enabled || $[38] !== togglePolicyEnabledState) {
      t19 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[37] = enabled;
      $[38] = togglePolicyEnabledState;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    const onToggleClicked = t19;
    const t20 = props.policy?.id || "";
    let T6;
    let T7;
    let t21;
    let t22;
    let t23;
    let t24;
    let t25;
    let t26;
    if ($[40] !== enabled || $[41] !== isAuthorized || $[42] !== isSaasWorkspace || $[43] !== props.onDelete || $[44] !== props.onDuplicate || $[45] !== props.onEdit || $[46] !== props.onEnabledPolicyUpdate || $[47] !== props.policy.attachedResources || $[48] !== props.policy.name || $[49] !== props.policy.policyType || $[50] !== props.processing || $[51] !== t20) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t20],
        property: backupPolicies.name
      };
      T5 = BPCard;
      t13 = !enabled;
      t14 = "backup-policy-card";
      T7 = Stack;
      t24 = "row";
      if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[63] = t25;
      } else {
        t25 = $[63];
      }
      let t27;
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[64] = t27;
      } else {
        t27 = $[64];
      }
      let t28;
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[65] = t28;
      } else {
        t28 = $[65];
      }
      let t29;
      if ($[66] !== props.policy.name) {
        t29 = <Stack sx={t28}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[66] = props.policy.name;
        $[67] = t29;
      } else {
        t29 = $[67];
      }
      let t30;
      if ($[68] !== props.policy.policyType) {
        t30 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[68] = props.policy.policyType;
        $[69] = t30;
      } else {
        t30 = $[69];
      }
      if ($[70] !== t29 || $[71] !== t30) {
        t26 = <Stack direction="row" sx={t27}>{t29}{t30}</Stack>;
        $[70] = t29;
        $[71] = t30;
        $[72] = t26;
      } else {
        t26 = $[72];
      }
      T6 = Stack;
      t21 = "row";
      if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = {
          alignItems: "center",
          gap: "16px"
        };
        $[73] = t22;
      } else {
        t22 = $[73];
      }
      t23 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader size="medium" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createBackupStatusNotInCondition([BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.Unsupported, BackupStatus.LimitExceeded])])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[40] = enabled;
      $[41] = isAuthorized;
      $[42] = isSaasWorkspace;
      $[43] = props.onDelete;
      $[44] = props.onDuplicate;
      $[45] = props.onEdit;
      $[46] = props.onEnabledPolicyUpdate;
      $[47] = props.policy.attachedResources;
      $[48] = props.policy.name;
      $[49] = props.policy.policyType;
      $[50] = props.processing;
      $[51] = t20;
      $[52] = T5;
      $[53] = T6;
      $[54] = T7;
      $[55] = t13;
      $[56] = t14;
      $[57] = t21;
      $[58] = t22;
      $[59] = t23;
      $[60] = t24;
      $[61] = t25;
      $[62] = t26;
    } else {
      T5 = $[52];
      T6 = $[53];
      T7 = $[54];
      t13 = $[55];
      t14 = $[56];
      t21 = $[57];
      t22 = $[58];
      t23 = $[59];
      t24 = $[60];
      t25 = $[61];
      t26 = $[62];
    }
    let t27;
    if ($[74] !== isAuthorized || $[75] !== isGoogleWorkspace || $[76] !== isSaasWorkspace || $[77] !== props) {
      t27 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? isGoogleWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[74] = isAuthorized;
      $[75] = isGoogleWorkspace;
      $[76] = isSaasWorkspace;
      $[77] = props;
      $[78] = t27;
    } else {
      t27 = $[78];
    }
    let t28;
    if ($[79] !== enabled || $[80] !== isAuthorized || $[81] !== onToggleClicked || $[82] !== props.onEnabledPolicyUpdate) {
      t28 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[79] = enabled;
      $[80] = isAuthorized;
      $[81] = onToggleClicked;
      $[82] = props.onEnabledPolicyUpdate;
      $[83] = t28;
    } else {
      t28 = $[83];
    }
    let t29;
    if ($[84] !== T6 || $[85] !== t21 || $[86] !== t22 || $[87] !== t23 || $[88] !== t27 || $[89] !== t28) {
      t29 = <T6 direction={t21} sx={t22}>{t23}{t27}{t28}</T6>;
      $[84] = T6;
      $[85] = t21;
      $[86] = t22;
      $[87] = t23;
      $[88] = t27;
      $[89] = t28;
      $[90] = t29;
    } else {
      t29 = $[90];
    }
    if ($[91] !== T7 || $[92] !== t24 || $[93] !== t25 || $[94] !== t26 || $[95] !== t29) {
      t12 = <T7 direction={t24} sx={t25}>{t26}{t29}</T7>;
      $[91] = T7;
      $[92] = t24;
      $[93] = t25;
      $[94] = t26;
      $[95] = t29;
      $[96] = t12;
    } else {
      t12 = $[96];
    }
    T4 = CardContent;
    if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        marginTop: "24px",
        "&&": {
          padding: "0 0 24px"
        }
      };
      $[97] = t11;
    } else {
      t11 = $[97];
    }
    T3 = Stack;
    if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Divider />;
      t9 = {
        gap: "12px"
      };
      $[98] = t8;
      $[99] = t9;
    } else {
      t8 = $[98];
      t9 = $[99];
    }
    let t30;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        px: "24px"
      };
      $[100] = t30;
    } else {
      t30 = $[100];
    }
    let t31;
    if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[101] = t31;
    } else {
      t31 = $[101];
    }
    let t32;
    if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = {
        gap: "24px"
      };
      $[102] = t32;
    } else {
      t32 = $[102];
    }
    let t33;
    if ($[103] !== allBackupPolicyProperties || $[104] !== props.policy.condition || $[105] !== props.policy.exclude || $[106] !== props.policy.include || $[107] !== props.policy.policyType || $[108] !== props.policy.resourceTypes || $[109] !== resourcesScopeType) {
      t33 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[103] = allBackupPolicyProperties;
      $[104] = props.policy.condition;
      $[105] = props.policy.exclude;
      $[106] = props.policy.include;
      $[107] = props.policy.policyType;
      $[108] = props.policy.resourceTypes;
      $[109] = resourcesScopeType;
      $[110] = t33;
    } else {
      t33 = $[110];
    }
    let t34;
    if ($[111] !== props.policy.policyType) {
      t34 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} />;
      $[111] = props.policy.policyType;
      $[112] = t34;
    } else {
      t34 = $[112];
    }
    let t35;
    if ($[113] !== t33 || $[114] !== t34) {
      t35 = <AccordionDetails><Stack sx={t32}>{t33}{t34}</Stack></AccordionDetails>;
      $[113] = t33;
      $[114] = t34;
      $[115] = t35;
    } else {
      t35 = $[115];
    }
    if ($[116] !== props.defaultExpanded || $[117] !== t35) {
      t10 = <Accordion defaultExpanded={props.defaultExpanded} sx={t30}>{t31}{t35}</Accordion>;
      $[116] = props.defaultExpanded;
      $[117] = t35;
      $[118] = t10;
    } else {
      t10 = $[118];
    }
    T2 = Accordion;
    t5 = vaultsSectionExpanded;
    if ($[119] !== vaultsSectionExpanded) {
      t6 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[119] = vaultsSectionExpanded;
      $[120] = t6;
    } else {
      t6 = $[120];
    }
    if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        px: "24px"
      };
      $[121] = t7;
    } else {
      t7 = $[121];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t0 = "row";
    t1 = "w-full";
    if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t3 = <Typography variant="h4">Backup schedules</Typography>;
      $[122] = t2;
      $[123] = t3;
    } else {
      t2 = $[122];
      t3 = $[123];
    }
    t4 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = allBackupPolicyProperties;
    $[1] = enabled;
    $[2] = isAuthorized;
    $[3] = props;
    $[4] = snackbar;
    $[5] = vaultsSectionExpanded;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = T3;
    $[10] = T4;
    $[11] = T5;
    $[12] = t0;
    $[13] = t1;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = togglePolicyEnabledState;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    T3 = $[9];
    T4 = $[10];
    T5 = $[11];
    t0 = $[12];
    t1 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
    togglePolicyEnabledState = $[27];
  }
  let t15;
  if ($[124] !== T0 || $[125] !== t0 || $[126] !== t1 || $[127] !== t2 || $[128] !== t3 || $[129] !== t4) {
    t15 = <T0 direction={t0} className={t1} sx={t2}>{t3}{t4}</T0>;
    $[124] = T0;
    $[125] = t0;
    $[126] = t1;
    $[127] = t2;
    $[128] = t3;
    $[129] = t4;
    $[130] = t15;
  } else {
    t15 = $[130];
  }
  let t16;
  if ($[131] !== T1 || $[132] !== t15) {
    t16 = <T1>{t15}</T1>;
    $[131] = T1;
    $[132] = t15;
    $[133] = t16;
  } else {
    t16 = $[133];
  }
  let t17;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[134] = t17;
  } else {
    t17 = $[134];
  }
  let t18;
  if ($[135] !== props.policy || $[136] !== props.vaults) {
    t18 = <AccordionDetails sx={t17}>{isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />}</AccordionDetails>;
    $[135] = props.policy;
    $[136] = props.vaults;
    $[137] = t18;
  } else {
    t18 = $[137];
  }
  let t19;
  if ($[138] !== T2 || $[139] !== t16 || $[140] !== t18 || $[141] !== t5 || $[142] !== t6 || $[143] !== t7) {
    t19 = <T2 expanded={t5} onChange={t6} sx={t7}>{t16}{t18}</T2>;
    $[138] = T2;
    $[139] = t16;
    $[140] = t18;
    $[141] = t5;
    $[142] = t6;
    $[143] = t7;
    $[144] = t19;
  } else {
    t19 = $[144];
  }
  let t20;
  if ($[145] !== T3 || $[146] !== t10 || $[147] !== t19 || $[148] !== t8 || $[149] !== t9) {
    t20 = <T3 divider={t8} sx={t9}>{t10}{t19}</T3>;
    $[145] = T3;
    $[146] = t10;
    $[147] = t19;
    $[148] = t8;
    $[149] = t9;
    $[150] = t20;
  } else {
    t20 = $[150];
  }
  let t21;
  if ($[151] !== T4 || $[152] !== t11 || $[153] !== t20) {
    t21 = <T4 sx={t11}>{t20}</T4>;
    $[151] = T4;
    $[152] = t11;
    $[153] = t20;
    $[154] = t21;
  } else {
    t21 = $[154];
  }
  let t22;
  if ($[155] !== props.policy.name) {
    t22 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[155] = props.policy.name;
    $[156] = t22;
  } else {
    t22 = $[156];
  }
  let t23;
  if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setDisablePolicyDialogOpen(false);
    $[157] = t23;
  } else {
    t23 = $[157];
  }
  let t24;
  if ($[158] !== togglePolicyEnabledState) {
    t24 = async () => {
      await togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[158] = togglePolicyEnabledState;
    $[159] = t24;
  } else {
    t24 = $[159];
  }
  let t25;
  if ($[160] !== disablePolicyDialogOpen || $[161] !== t22 || $[162] !== t24) {
    t25 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t22} onCancel={t23} onConfirm={t24} buttonTitle="Disable Policy" />;
    $[160] = disablePolicyDialogOpen;
    $[161] = t22;
    $[162] = t24;
    $[163] = t25;
  } else {
    t25 = $[163];
  }
  let t26;
  if ($[164] !== t12 || $[165] !== t21 || $[166] !== t25) {
    t26 = <>{t12}{t21}{t25}</>;
    $[164] = t12;
    $[165] = t21;
    $[166] = t25;
    $[167] = t26;
  } else {
    t26 = $[167];
  }
  let t27;
  if ($[168] !== T5 || $[169] !== t13 || $[170] !== t14 || $[171] !== t26) {
    t27 = <T5 disabled={t13} data-testid={t14}>{t26}</T5>;
    $[168] = T5;
    $[169] = t13;
    $[170] = t14;
    $[171] = t26;
    $[172] = t27;
  } else {
    t27 = $[172];
  }
  return t27;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}